import type Phaser from 'phaser';
import type { IncidentKind } from '@core/mission/incident';
import type { OutcomeTier } from '@core/mission/outcome';
import type { VesselId } from '@core/voyage/profiles';
import { PORTRAIT_IDS, ADVISER_PORTRAIT_ID } from '@core/character/character';

/**
 * Das Bildregister.
 *
 * Die Dateien liegen unter `public/assets/` und werden von
 * `tools/fetch_assets.py` erzeugt — vorbereitet, entsaettigt und gekoernt, alle
 * durch dieselbe Kurve. Sie stehen bewusst *nicht* im Modulgraphen: Vite wuerde
 * sie sonst hashen und in den Abhaengigkeitsbaum ziehen, obwohl sie nur einmal
 * beim Start geladen werden.
 *
 * `BASE_URL` steht davor, damit das Spiel auch aus einem Unterverzeichnis
 * laeuft — dieselbe Ueberlegung wie bei `base: './'` in der Vite-Konfiguration.
 */

const ROOT = `${import.meta.env.BASE_URL}assets/`;

export const portraitUrl = (id: string): string => `${ROOT}portraits/${id}.jpg`;
export const plateUrl = (id: string): string => `${ROOT}plates/${id}.jpg`;
export const backdropUrl = (id: string): string => `${ROOT}backdrops/${id}.jpg`;

/** Texturschluessel. Getrennt von den Kennungen, damit sich Portraetkennung
 * und Fahrzeugkennung nie in die Quere kommen koennen. */
export const portraitKey = (id: string): string => `portrait:${id}`;
export const plateKey = (id: string): string => `plate:${id}`;
export const backdropKey = (id: string): string => `backdrop:${id}`;

/** Ein Bild je Vorfalltyp — dieselbe Kennung wie im Manifest. */
export const INCIDENT_PLATE: Readonly<Record<IncidentKind, string>> = {
  cargo_vessel: 'cargo_vessel',
  fishing_boat: 'fishing_boat',
  private_yacht: 'private_yacht',
  airliner: 'airliner',
  light_aircraft: 'light_aircraft',
  naval_patrol: 'naval_patrol',
};

/** Ein Bild je erreichter Stufe. Es zeigt, was man vorfindet, nicht wen man sucht. */
export const TIER_PLATE: Readonly<Record<OutcomeTier, string>> = {
  rescue: 'site_rescue',
  salvage: 'site_salvage',
  cold: 'site_cold',
  nothing: 'site_nothing',
};

/** Die eigenen Fahrzeuge. */
export const VESSEL_PLATE: Readonly<Record<VesselId, string>> = {
  ship: 'research_vessel',
  submarine: 'submarine',
};

export const MENU_BACKDROP = 'menu_wreck';
/** Eine alte Seekarte — der Untergrund, auf dem die Funde liegen. */
export const EVIDENCE_BACKDROP = 'evidence_chart';
/** Morgengrauen ueber offener See. Das Letzte, was man von diesem Fall sieht. */
export const FINALE_BACKDROP = 'finale_dawn';

export const BACKDROPS = [MENU_BACKDROP, EVIDENCE_BACKDROP, FINALE_BACKDROP] as const;

/**
 * Legt einen Hintergrund formatfuellend hinter die Szene.
 *
 * Zugeschnitten statt verzerrt, stark abgedunkelt und ganz nach hinten: Das
 * Bild traegt die Stimmung, aber der Text darauf muss lesbar bleiben. Fehlt
 * die Datei, passiert nichts — die Szene sieht dann aus wie vor M7.
 */
export function addBackdrop(
  scene: Phaser.Scene,
  id: string,
  alpha: number,
): Phaser.GameObjects.Image | null {
  const key = backdropKey(id);
  if (!scene.textures.exists(key)) return null;

  const { width, height } = scene.scale;
  const image = scene.add.image(width / 2, height / 2, key).setDepth(-40);
  image.setScale(Math.max(width / image.width, height / image.height));
  image.setAlpha(alpha);
  return image;
}

/**
 * Reiht alles beim Start in den Phaser-Lader ein.
 *
 * Zusammen gut anderthalb Megabyte — wenig genug, um es in einem Zug zu laden,
 * und dafuer ist danach kein Nachladen mehr noetig, das mitten in einer Szene
 * ins Leere liefe.
 */
export function queueImages(load: Phaser.Loader.LoaderPlugin): void {
  for (const id of [...PORTRAIT_IDS, ADVISER_PORTRAIT_ID]) {
    load.image(portraitKey(id), portraitUrl(id));
  }
  for (const id of [
    ...Object.values(INCIDENT_PLATE),
    ...Object.values(TIER_PLATE),
    ...Object.values(VESSEL_PLATE),
  ]) {
    load.image(plateKey(id), plateUrl(id));
  }
  for (const id of BACKDROPS) {
    load.image(backdropKey(id), backdropUrl(id));
  }
}
